import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { INDIAN_STATES } from '../constants/indianStates';

const CERT_OPTIONS = [
  { value: 'basic',                     label: 'Basic Pranic Healing' },
  { value: 'advanced',                  label: 'Advanced Pranic Healing' },
  { value: 'psychotherapy',             label: 'Pranic Psychotherapy' },
  { value: 'arhatic',                   label: 'Arhatic Yoga' },
  { value: 'certified_healer',          label: 'Certified Pranic Healer' },
  { value: 'advanced_certified',        label: 'Associate Certified Pranic Healer' },
  { value: 'certified_psychotherapist', label: 'Certified Pranic Psychotherapist' },
];


export default function SoloRegisterPage() {
  const navigate       = useNavigate();
  const [searchParams] = useSearchParams();
  const refCode        = searchParams.get('referralCode') || '';
  const [form,         setForm]         = useState({
    firstName: '', lastName: '', email: '', password: '',
    whatsappNumber: '', city: '', state: '', certLevel: '', bio: '', certPhotoUrl: ''
  });
  const [submitting,           setSubmitting]           = useState(false);
  const [errorMsg,             setErrorMsg]             = useState('');
  const [certUploadAttempted,  setCertUploadAttempted]  = useState(false);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleCertUpload = async e => {
    const file = e.target.files[0];
    if (!file) return;
    setCertUploadAttempted(true);
    setErrorMsg('');
    console.log('[cert-debug] file present:', !!file, file?.name, file?.size);
    const fd = new FormData();
    fd.append('certificate', file);
    try {
      const r = await api.post('/auth/upload-cert', fd, {
        headers: { 'Content-Type': undefined },
      });
      setForm(f => ({ ...f, certPhotoUrl: r.data.certPhotoUrl }));
    } catch (err) {
      console.error('[cert-debug] upload failed:', err.response?.status, err.response?.data, err.message);
      const status = err.response?.status;
      if (status === 429) {
        setErrorMsg('Too many attempts. Please wait a few minutes and try again.');
      } else if (status === 413) {
        setErrorMsg('File is too large. Please use a smaller image.');
      } else if (status === 400) {
        setErrorMsg(err.response?.data?.message || 'Certificate upload failed. Please try again.');
      } else {
        setErrorMsg('Certificate upload failed. Please try again.');
      }
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.firstName.trim())      { setErrorMsg('First name is required.'); return; }
    if (!form.email.trim())          { setErrorMsg('Email is required.'); return; }
    if (!form.password)              { setErrorMsg('Password is required.'); return; }
    if (!form.whatsappNumber.trim()) { setErrorMsg('WhatsApp number is required.'); return; }
    if (!form.city.trim())           { setErrorMsg('City is required.'); return; }
    if (!form.state)                 { setErrorMsg('Please select your state.'); return; }
    if (!form.certLevel.trim())      { setErrorMsg('Certification level is required.'); return; }
    if (!form.certPhotoUrl) {
      if (!certUploadAttempted) setErrorMsg('Certification photo is required.');
      // upload was attempted but failed — preserve the specific upload error already shown
      return;
    }

    setErrorMsg('');
    setSubmitting(true);
    try {
      await api.post('/solo/register', {
        ...form,
        plan: 'SOLO_FREE',
        ...(refCode && { referralCode: refCode }),
      });
      navigate('/register/success');
    } catch (err) {
      setErrorMsg(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={S.page}>
      <div style={S.card}>
        <h1 style={S.title}>Create Your Healer Profile</h1>
        <div style={S.steps}>
          <span style={S.stepActive}>1. Your Details</span>
          <span style={S.stepDivider}>›</span>
          <span style={S.stepInactive}>2. Complete</span>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          {errorMsg && <div style={S.errorBanner}>{errorMsg}</div>}

          {refCode && (
            <div style={S.referralBanner}>
              You've been invited via the LifeChiAI Refer &amp; Earn programme. By registering,
              you confirm that a LifeChiAI healer referred you. The referrer may receive a reward
              when you complete registration.{' '}
              <a href="https://www.lifechiai.com/privacy" target="_blank" rel="noopener noreferrer" style={S.referralBannerLink}>
                Privacy Policy
              </a>
            </div>
          )}

          <label style={S.label}>First Name *</label>
          <input name="firstName" value={form.firstName} onChange={handleChange} style={S.input} autoComplete="given-name" />

          <label style={S.label}>Last Name</label>
          <input name="lastName" value={form.lastName} onChange={handleChange} style={S.input} autoComplete="family-name" />

          <label style={S.label}>Email *</label>
          <input name="email" value={form.email} onChange={handleChange} style={S.input} type="email" autoComplete="email" />

          <label style={S.label}>Password *</label>
          <input name="password" value={form.password} onChange={handleChange} style={S.input} type="password" autoComplete="new-password" />

          <label style={S.label}>WhatsApp Number * (with country code)</label>
          <input name="whatsappNumber" value={form.whatsappNumber} onChange={handleChange} style={S.input} placeholder="+919876543210" type="tel" />

          <label style={S.label}>City *</label>
          <input name="city" value={form.city} onChange={handleChange} style={S.input} autoComplete="address-level2" />

          <label style={S.label}>State *</label>
          <select name="state" value={form.state} onChange={handleChange} style={S.input} required>
            <option value="">Select state</option>
            {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>

          <label style={S.label}>Certification Level *</label>
          <select name="certLevel" value={form.certLevel} onChange={handleChange} style={S.input}>
            <option value="">Select certification level</option>
            {CERT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>

          <label style={S.label}>Certification Photo *</label>
          <input type="file" accept="image/jpeg,image/png,image/heic"
            onChange={handleCertUpload} style={S.fileInput} />
          {form.certPhotoUrl && (
            <img src={form.certPhotoUrl} alt="Certificate preview" style={S.certPreview} />
          )}

          <label style={S.label}>Bio (optional)</label>
          <textarea name="bio" value={form.bio} onChange={handleChange} style={S.textarea} rows={3} placeholder="Briefly describe your practice" />

          <button type="submit" style={S.btn} disabled={submitting}>
            {submitting ? 'Creating…' : 'Create Account'}
          </button>
        </form>
      </div>
    </div>
  );
}

const S = {
  page:        { minHeight: '100vh', backgroundColor: '#f0faf6', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '24px 16px' },
  card:        { backgroundColor: '#fff', borderRadius: 16, padding: 28, width: '100%', maxWidth: 480, boxShadow: '0 2px 16px rgba(0,0,0,0.08)' },
  title:       { fontSize: 22, fontWeight: 800, color: '#1a3d2b', marginBottom: 8 },
  steps:       { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, fontSize: 13 },
  stepActive:  { fontWeight: 700, color: '#1a6b5a' },
  stepInactive:{ color: '#9ca3af' },
  stepDivider: { color: '#d1d5db' },
  label:       { display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 6, marginTop: 14 },
  input:       { width: '100%', padding: '12px 14px', fontSize: 16, border: '1.5px solid #d1d5db', borderRadius: 8, boxSizing: 'border-box', outline: 'none' },
  textarea:    { width: '100%', padding: '12px 14px', fontSize: 16, border: '1.5px solid #d1d5db', borderRadius: 8, boxSizing: 'border-box', resize: 'vertical', outline: 'none' },
  btn:         { marginTop: 16, width: '100%', padding: '14px 0', backgroundColor: '#1a6b5a', color: '#fff', fontSize: 16, fontWeight: 700, border: 'none', borderRadius: 10, cursor: 'pointer' },
  errorBanner: { backgroundColor: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626', borderRadius: 8, padding: '10px 14px', fontSize: 14, marginBottom: 12 },
  referralBanner:     { backgroundColor: '#f0faf6', border: '1px solid #a7d7c5', color: '#1a3d2b', borderRadius: 8, padding: '10px 14px', fontSize: 13, lineHeight: '1.5', marginBottom: 12 },
  referralBannerLink: { color: '#1a6b5a', fontWeight: 600 },
  fileInput:          { width: '100%', padding: '10px 0', fontSize: 15, cursor: 'pointer' },
  certPreview:        { width: '100%', maxHeight: 160, objectFit: 'contain', borderRadius: 8, border: '1px solid #d1d5db', marginTop: 8 },
};
